import { useEffect, useMemo, useState } from "react";
import { apiFetch, API_URL } from "../api/api";

function downloadCSV(url: string, filename: string) {
  const token = localStorage.getItem("token");
  fetch(url, { headers: { Authorization: `Bearer ${token}` } })
    .then(r => r.blob())
    .then(blob => {
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      a.click();
    });
}

export default function MonitoringJournalMakanHarian() {
  const today = new Date().toISOString().split("T")[0];

  const [tanggal, setTanggal] = useState(today);
  const [rows, setRows] = useState<any[]>([]);
  const [summary, setSummary] = useState<any>(null);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const [expStart, setExpStart] = useState("2026-01-04");
  const [expEnd, setExpEnd] = useState("2026-04-04");
  const [showExport, setShowExport] = useState(false);

  const pageSize = 8;

  useEffect(() => {
    apiFetch(`/admin/monitoring/journal-makan/harian?tanggal=${tanggal}`).then(res => {
      setRows(res.data ?? []);
      setSummary(res.summary);
      setPage(1);
      setSearch("");
    });
  }, [tanggal]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return rows.filter(r =>
      (r.nama_panggilan ?? "").toLowerCase().includes(q) ||
      (r.nama_lengkap ?? "").toLowerCase().includes(q) ||
      (r.alamat ?? "").toLowerCase().includes(q)
    );
  }, [rows, search]);

  const totalPage = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(page, totalPage);
  const pagedData = filtered.slice((safePage - 1) * pageSize, safePage * pageSize);

  const handleExport = () => {
    const url = `${API_URL}/admin/export/jurnal-makan?start=${expStart}&end=${expEnd}`;
    downloadCSV(url, `jurnal-makan-${expStart}-${expEnd}.csv`);
  };

  return (
    <div className="space-y-6">
      {/* ─── HEADER ─── */}
      <div className="bg-white rounded-2xl shadow p-6">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-purple-700">Monitoring Jurnal Makan Harian</h1>
            <p className="text-sm text-gray-500">Pantau pengisian jurnal makan ibu</p>
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            <input type="date" value={tanggal}
              onChange={e => setTanggal(e.target.value)}
              className="border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-400 outline-none" />
            <input type="text" placeholder="Cari nama / alamat..."
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(1); }}
              className="border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-400 outline-none" />
            <button
              onClick={() => setShowExport(v => !v)}
              className="flex items-center gap-1 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-xl text-sm shadow transition"
            >
              <ExcelIcon className="w-4 h-4" />
              Export Excel
            </button>
          </div>
        </div>

        {showExport && (
          <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-xl flex flex-wrap gap-3 items-end">
            <div>
              <label className="text-xs text-gray-600 block mb-1">Tanggal Mulai</label>
              <input type="date" value={expStart} onChange={e => setExpStart(e.target.value)}
                className="border rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-green-400" />
            </div>
            <div>
              <label className="text-xs text-gray-600 block mb-1">Tanggal Selesai</label>
              <input type="date" value={expEnd} onChange={e => setExpEnd(e.target.value)}
                className="border rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-green-400" />
            </div>
            <button onClick={handleExport}
              className="bg-green-600 hover:bg-green-700 text-white px-5 py-2 rounded-xl text-sm shadow transition">
              Download CSV
            </button>
          </div>
        )}
      </div>

      {/* ─── SUMMARY ─── */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <SummaryCard title="Total Ibu" value={summary.total_ibu} color="from-purple-500 to-purple-600" />
          <SummaryCard title="Sudah Mengisi" value={summary.sudah_mengisi} color="from-green-500 to-green-600" />
          <SummaryCard title="Belum Mengisi" value={summary.belum_mengisi} color="from-red-500 to-red-600" />
        </div>
      )}

      {/* ─── TABLE ─── */}
      <div className="bg-white rounded-2xl shadow overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gradient-to-r from-purple-500 to-purple-600 text-white">
            <tr>
              <th className="px-4 py-3 text-left w-10">No</th>
              <th className="px-4 py-3 text-left">Nama Ibu</th>
              <th className="px-4 py-3 text-left">Nama Lengkap</th>
              <th className="px-4 py-3 text-left">Usia</th>
              <th className="px-4 py-3 text-left">Status</th>
              <th className="px-4 py-3 text-left">Jurnal Makan</th>
            </tr>
          </thead>
          <tbody>
            {pagedData.map((r, i) => (
              <tr key={r.user_id}
                className={`border-b hover:bg-purple-50 transition ${i % 2 === 0 ? "bg-white" : "bg-gray-50"}`}>
                <td className="px-4 py-3 text-gray-400 text-center">
                  {(safePage - 1) * pageSize + i + 1}
                </td>
                <td className="px-4 py-3 font-medium text-gray-700">{r.nama_panggilan ?? "-"}</td>
                <td className="px-4 py-3 text-gray-600">{r.nama_lengkap ?? "-"}</td>
                <td className="px-4 py-3 text-gray-600">{r.usia ?? "-"}</td>
                <td className="px-4 py-3">
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                    r.status_isi === "sudah" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
                  }`}>
                    {r.status_isi === "sudah" ? "✓ Sudah" : "✗ Belum"}
                  </span>
                </td>
                <td className="px-4 py-3 text-gray-600">
                  {r.status_isi === "sudah" ? (
                    <ul className="list-none space-y-0.5 text-xs">
                      <li>🍳 <span className="font-medium">Sarapan:</span> {r.sarapan || "-"}</li>
                      <li>🍎 <span className="font-medium">Snack 1:</span> {r.snack1 || "-"}</li>
                      <li>🍛 <span className="font-medium">Siang:</span> {r.makan_siang || "-"}</li>
                      <li>🥛 <span className="font-medium">Snack 2:</span> {r.snack2 || "-"}</li>
                      <li>🍽️ <span className="font-medium">Malam:</span> {r.makan_malam || "-"}</li>
                    </ul>
                  ) : (
                    <span className="italic text-gray-400 text-xs">Belum ada jurnal</span>
                  )}
                </td>
              </tr>
            ))}
            {pagedData.length === 0 && (
              <tr>
                <td colSpan={6} className="text-center py-12 text-gray-400 italic">
                  Data tidak ditemukan
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* ─── PAGINATION ─── */}
      <div className="flex flex-col sm:flex-row justify-between items-center gap-3">
        <p className="text-sm text-gray-500">
          Menampilkan <span className="font-semibold text-purple-600">{pagedData.length}</span>{" "}
          dari <span className="font-semibold">{filtered.length}</span> data
        </p>
        <div className="flex items-center gap-1">
          <button disabled={safePage === 1} onClick={() => setPage(1)}
            className="px-3 py-1.5 rounded-lg border text-sm disabled:opacity-30 hover:bg-purple-50 transition">«</button>
          <button disabled={safePage === 1} onClick={() => setPage(p => Math.max(1, p - 1))}
            className="px-3 py-1.5 rounded-lg border text-sm disabled:opacity-30 hover:bg-purple-50 transition">‹</button>
          {Array.from({ length: totalPage }, (_, i) => i + 1)
            .filter(p => p === 1 || p === totalPage || Math.abs(p - safePage) <= 2)
            .reduce<(number | "...")[]>((acc, p, idx, arr) => {
              if (idx > 0 && p - (arr[idx - 1] as number) > 1) acc.push("...");
              acc.push(p);
              return acc;
            }, [])
            .map((p, i) =>
              p === "..." ? (
                <span key={`e${i}`} className="px-2 text-gray-400">…</span>
              ) : (
                <button key={p} onClick={() => setPage(p as number)}
                  className={`px-3 py-1.5 rounded-lg border text-sm transition ${
                    safePage === p ? "bg-purple-600 text-white border-purple-600" : "hover:bg-purple-50"
                  }`}>{p}</button>
              )
            )}
          <button disabled={safePage === totalPage} onClick={() => setPage(p => Math.min(totalPage, p + 1))}
            className="px-3 py-1.5 rounded-lg border text-sm disabled:opacity-30 hover:bg-purple-50 transition">›</button>
          <button disabled={safePage === totalPage} onClick={() => setPage(totalPage)}
            className="px-3 py-1.5 rounded-lg border text-sm disabled:opacity-30 hover:bg-purple-50 transition">»</button>
        </div>
      </div>
    </div>
  );
}

function SummaryCard({ title, value, color }: { title: string; value: number; color: string }) {
  return (
    <div className={`bg-gradient-to-r ${color} text-white rounded-2xl p-5 shadow`}>
      <p className="text-sm opacity-90">{title}</p>
      <p className="text-3xl font-bold">{value}</p>
    </div>
  );
}
function ExcelIcon(props: any) {
  return (
    <svg {...props} viewBox="0 0 24 24" fill="currentColor">
      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6zm-1 1.5L18.5 9H13V3.5zM8.5 17l2-3-2-3h1.8l1.2 2 1.2-2H14l-2.1 3L14 17h-1.8l-1.2-2-1.2 2H8.5z"/>
    </svg>
  );
}
